"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";

// Global top-of-page progress line for client-side navigation. App Router
// has no public router-events API (unlike the old Pages Router), so this
// starts on any internal <a>/Link click (capturing the click at the
// document level, rather than wrapping every Link in the app) and
// completes once `usePathname()` / `useSearchParams()` reflect the new route
// having rendered. A link to the page already open (same path and query)
// doesn't start it, since nothing would ever complete it; and a stuck bar
// gives up after STUCK_MS whatever happened (e.g. a navigation cancelled).
const STUCK_MS = 10_000;

export function RouteProgress() {
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stuckRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const link = (event.target as HTMLElement)?.closest("a");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href || href.startsWith("#") || link.target === "_blank" || link.hasAttribute("download")) return;
      if (/^[a-z]+:/i.test(href) && !href.startsWith(window.location.origin)) return;
      const target = new URL(href, window.location.href);
      if (target.pathname === window.location.pathname && target.search === window.location.search) return;

      setVisible(true);
      setProgress(15);
      if (intervalRef.current) clearInterval(intervalRef.current);
      // Creeps toward 85% and waits there — never claims "done" until the
      // route actually finishes (see the pathname effect below).
      intervalRef.current = setInterval(() => {
        setProgress((p) => (p < 85 ? p + (85 - p) * 0.1 : p));
      }, 200);
      if (stuckRef.current) clearTimeout(stuckRef.current);
      stuckRef.current = setTimeout(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setVisible(false);
        setProgress(0);
      }, STUCK_MS);
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (stuckRef.current) clearTimeout(stuckRef.current);
    setProgress(100);
    const timeout = setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 250);
    return () => clearTimeout(timeout);
  }, [pathname, search]);

  if (!visible) return null;

  // Gold (loading states round 3): it reads on the canvas and on the installed
  // app's navy status bar, where the navy line it was disappeared.

  return (
    <div className="fixed inset-x-0 top-0 z-50 h-1.5" aria-hidden>
      <div
        className="bg-brand-strong h-full transition-[width] duration-200 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
